/**
 * Drizzle query builders (over local PowerSync/SQLite) for the FieldCheck domain.
 *
 * No filter by user_id: the local DB only contains what the sync rules already
 * authorize (my rows + those of my organizations) — same criterion as the
 * template's lists/todos queries.
 */

import { and, asc, type Column, desc, eq, isNull, type SQL, sql } from "drizzle-orm";
import {
  attachment,
  finding,
  inspection,
  inspection_answer,
  member,
  organization,
  type PowerSyncDrizzleDB,
  site,
  template,
  template_item,
  template_version,
  user,
} from "@fieldcheck/powersync";
import { liveFindingFilters, liveInspectionFilters, liveTemplateFilters } from "../live/filters";

// ─── Active organization scope (multi-org switcher) ──────────────────────────
//
// The model is already org-centric: each row carries `organization_id` (a real org)
// or `NULL` (PERSONAL resource, only mine). The local replica downloads EVERYTHING the
// sync rules authorize (my rows + those of ALL my orgs), so without a
// scope filter the views mix the activity of all the organizations.
// The active context switcher narrows the view to ONE scope: "Personal"
// (`organization_id IS NULL`) or a specific org (`organization_id = <id>`).
//
// Optional `scope` ⇒ backward compatible: without scope, the query does not filter by org
// (the always-on merged view). Personal is modeled as `organizationId: null`.

/** Active scope of the switcher: an organization (`organizationId`) or Personal (`null`). */
export interface OrgScope {
  organizationId: string | null;
}

/**
 * Drizzle condition that narrows an `organization_id` column to the active scope.
 * `undefined` ⇒ no filter (all scopes). Personal (`organizationId: null`)
 * ⇒ `IS NULL`; a specific org ⇒ equality. PURE and testable (SQL renderable
 * with `SQLiteSyncDialect`) — the rest of the list-queries reuse it.
 */
export function orgScopeCondition(column: Column, scope?: OrgScope): SQL | undefined {
  if (!scope) {
    return undefined;
  }
  return scope.organizationId === null ? isNull(column) : eq(column, scope.organizationId);
}

// ─── Sites ───────────────────────────────────────────────────────────────────

export function getSitesQuery(
  db: PowerSyncDrizzleDB,
  options?: { includeArchived?: boolean; scope?: OrgScope },
) {
  const conditions: SQL[] = [];
  if (!options?.includeArchived) {
    conditions.push(sql`COALESCE(${site.archived}, 0) = 0`);
  }
  const scopeCond = orgScopeCondition(site.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  const base = db.select().from(site);
  const query = conditions.length > 0 ? base.where(and(...conditions)) : base;
  return query.orderBy(asc(site.name));
}

export function getSiteByIdQuery(db: PowerSyncDrizzleDB, siteId: string) {
  return db.select().from(site).where(eq(site.id, siteId));
}

// ─── Templates ───────────────────────────────────────────────────────────────

export function getTemplatesQuery(
  db: PowerSyncDrizzleDB,
  options?: { includeArchived?: boolean; scope?: OrgScope },
) {
  // Live view: excludes the trash (live layer) ALWAYS, archived depending on option.
  const conditions: SQL[] = [...liveTemplateFilters()];
  if (!options?.includeArchived) {
    conditions.push(sql`COALESCE(${template.archived}, 0) = 0`);
  }
  const scopeCond = orgScopeCondition(template.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  return db
    .select()
    .from(template)
    .where(and(...conditions))
    .orderBy(desc(template.updated_at));
}

export function getTemplateByIdQuery(db: PowerSyncDrizzleDB, templateId: string) {
  return db.select().from(template).where(eq(template.id, templateId));
}

export function getTemplateItemsQuery(db: PowerSyncDrizzleDB, templateId: string) {
  return db
    .select()
    .from(template_item)
    .where(eq(template_item.template_id, templateId))
    .orderBy(asc(template_item.sort_order), asc(template_item.created_at));
}

/**
 * LOCAL version history of a template (TEMPLATE VERSIONING) — PowerSync bucket
 * bounded to the sync window (last 10). UUIDv7 id ⇒ descending order = most
 * recent first. The deep history is requested by API
 * (GET /api/fieldcheck/template/:id/versions).
 */
export function getTemplateVersionsQuery(db: PowerSyncDrizzleDB, templateId: string) {
  return db
    .select()
    .from(template_version)
    .where(eq(template_version.template_id, templateId))
    .orderBy(desc(template_version.id));
}

// ─── Organizations (LOCAL read, offline-first) ───────────────────────────────

/** User's organizations, from the local replica (`organizations` stream). */
export function getMyOrganizationsLocalQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(organization).orderBy(asc(organization.name));
}

/** Members of an organization with their public info (name/email). */
export function getOrgMembersLocalQuery(db: PowerSyncDrizzleDB, organizationId: string) {
  return db
    .select({
      member_id: member.id,
      user_id: member.user_id,
      role: member.role,
      name: user.name,
      email: user.email,
    })
    .from(member)
    .leftJoin(user, eq(member.user_id, user.id))
    .where(eq(member.organization_id, organizationId))
    .orderBy(asc(user.name));
}

// ─── Inspections ─────────────────────────────────────────────────────────────

export function getInspectionsQuery(
  db: PowerSyncDrizzleDB,
  options?: { status?: string; scope?: OrgScope },
) {
  const conditions: SQL[] = [...liveInspectionFilters()];
  if (options?.status) {
    conditions.push(eq(inspection.status, options.status));
  }
  const scopeCond = orgScopeCondition(inspection.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  return db
    .select()
    .from(inspection)
    .where(and(...conditions))
    .orderBy(desc(inspection.updated_at));
}

/** Inspections with template and site name (main listing). */
export function getInspectionsWithMetaQuery(
  db: PowerSyncDrizzleDB,
  options?: { status?: string; scope?: OrgScope },
) {
  const base = db
    .select({
      id: inspection.id,
      template_id: inspection.template_id,
      site_id: inspection.site_id,
      user_id: inspection.user_id,
      organization_id: inspection.organization_id,
      inspector_id: inspection.inspector_id,
      status: inspection.status,
      notes: inspection.notes,
      started_at: inspection.started_at,
      completed_at: inspection.completed_at,
      signed_at: inspection.signed_at,
      created_at: inspection.created_at,
      updated_at: inspection.updated_at,
      deleted_at: inspection.deleted_at,
      template_name: template.name,
      site_name: site.name,
      inspector_name: user.name,
    })
    .from(inspection)
    .leftJoin(template, eq(inspection.template_id, template.id))
    .leftJoin(site, eq(inspection.site_id, site.id))
    .leftJoin(user, eq(inspection.inspector_id, user.id));
  const conditions: SQL[] = [...liveInspectionFilters()];
  if (options?.status) {
    conditions.push(eq(inspection.status, options.status));
  }
  const scopeCond = orgScopeCondition(inspection.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  return base.where(and(...conditions)).orderBy(desc(inspection.updated_at));
}

export type InspectionWithMeta = {
  id: string;
  template_id: string | null;
  site_id: string | null;
  user_id: string | null;
  organization_id: string | null;
  inspector_id: string | null;
  status: string | null;
  notes: string | null;
  started_at: string | null;
  completed_at: string | null;
  signed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  template_name: string | null;
  site_name: string | null;
  inspector_name: string | null;
};

export function getInspectionByIdQuery(db: PowerSyncDrizzleDB, inspectionId: string) {
  return db.select().from(inspection).where(eq(inspection.id, inspectionId));
}

/** Inspection detail with template/site/inspector name. */
export function getInspectionWithMetaByIdQuery(db: PowerSyncDrizzleDB, inspectionId: string) {
  return db
    .select({
      id: inspection.id,
      template_id: inspection.template_id,
      site_id: inspection.site_id,
      user_id: inspection.user_id,
      organization_id: inspection.organization_id,
      inspector_id: inspection.inspector_id,
      status: inspection.status,
      notes: inspection.notes,
      started_at: inspection.started_at,
      completed_at: inspection.completed_at,
      signed_at: inspection.signed_at,
      created_at: inspection.created_at,
      updated_at: inspection.updated_at,
      deleted_at: inspection.deleted_at,
      template_name: template.name,
      site_name: site.name,
      inspector_name: user.name,
    })
    .from(inspection)
    .leftJoin(template, eq(inspection.template_id, template.id))
    .leftJoin(site, eq(inspection.site_id, site.id))
    .leftJoin(user, eq(inspection.inspector_id, user.id))
    .where(eq(inspection.id, inspectionId));
}

export function getAnswersByInspectionQuery(db: PowerSyncDrizzleDB, inspectionId: string) {
  return db
    .select()
    .from(inspection_answer)
    .where(eq(inspection_answer.inspection_id, inspectionId))
    .orderBy(asc(inspection_answer.created_at));
}

/** Count per status for the dashboard ({ status, count }). */
export function getInspectionStatusCountsQuery(
  db: PowerSyncDrizzleDB,
  options?: { scope?: OrgScope },
) {
  const conditions: SQL[] = [...liveInspectionFilters()];
  const scopeCond = orgScopeCondition(inspection.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  return db
    .select({
      status: inspection.status,
      count: sql<number>`COUNT(*)`.as("count"),
    })
    .from(inspection)
    .where(and(...conditions))
    .groupBy(inspection.status);
}

// ─── Findings ────────────────────────────────────────────────────────────────
// The "live view" filter for findings (out of trash + cohort of the
// inspection in the trash) lives in the live layer: `liveFindingFilters()`.

export function getFindingsQuery(
  db: PowerSyncDrizzleDB,
  options?: { status?: string; severity?: string; scope?: OrgScope },
) {
  const conditions: SQL[] = [];
  if (options?.status) {
    conditions.push(eq(finding.status, options.status));
  }
  if (options?.severity) {
    conditions.push(eq(finding.severity, options.severity));
  }
  const scopeCond = orgScopeCondition(finding.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  const base = db.select().from(finding);
  const query = base.where(and(...liveFindingFilters(), ...conditions));
  return query.orderBy(desc(finding.updated_at));
}

/** Findings with site and assignee name (listing/CSV). */
export function getFindingsWithMetaQuery(
  db: PowerSyncDrizzleDB,
  options?: { status?: string; severity?: string; scope?: OrgScope },
) {
  const conditions: SQL[] = [];
  if (options?.status) {
    conditions.push(eq(finding.status, options.status));
  }
  if (options?.severity) {
    conditions.push(eq(finding.severity, options.severity));
  }
  const scopeCond = orgScopeCondition(finding.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  const base = db
    .select({
      id: finding.id,
      inspection_id: finding.inspection_id,
      inspection_answer_id: finding.inspection_answer_id,
      site_id: finding.site_id,
      user_id: finding.user_id,
      organization_id: finding.organization_id,
      title: finding.title,
      description: finding.description,
      severity: finding.severity,
      status: finding.status,
      assignee_id: finding.assignee_id,
      due_date: finding.due_date,
      closed_at: finding.closed_at,
      created_at: finding.created_at,
      updated_at: finding.updated_at,
      deleted_at: finding.deleted_at,
      site_name: site.name,
      assignee_name: user.name,
    })
    .from(finding)
    .leftJoin(site, eq(finding.site_id, site.id))
    .leftJoin(user, eq(finding.assignee_id, user.id));
  const query = base.where(and(...liveFindingFilters(), ...conditions));
  return query.orderBy(desc(finding.updated_at));
}

export type FindingWithMeta = {
  id: string;
  inspection_id: string | null;
  inspection_answer_id: string | null;
  site_id: string | null;
  user_id: string | null;
  organization_id: string | null;
  title: string | null;
  description: string | null;
  severity: string | null;
  status: string | null;
  assignee_id: string | null;
  due_date: string | null;
  closed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  site_name: string | null;
  assignee_name: string | null;
};

export function getFindingByIdQuery(db: PowerSyncDrizzleDB, findingId: string) {
  return db.select().from(finding).where(eq(finding.id, findingId));
}

export function getFindingsByInspectionQuery(db: PowerSyncDrizzleDB, inspectionId: string) {
  return db
    .select()
    .from(finding)
    .where(and(eq(finding.inspection_id, inspectionId), ...liveFindingFilters()))
    .orderBy(desc(finding.created_at));
}

/**
 * PENDING (unresolved) findings by severity ({ severity, count }) for
 * the dashboard. Counts open + in_progress; excludes the terminal status
 * 'resolved' and the legacy 'closed'.
 */
export function getOpenFindingCountsQuery(db: PowerSyncDrizzleDB, options?: { scope?: OrgScope }) {
  const conditions: SQL[] = [
    ...liveFindingFilters(),
    sql`${finding.status} NOT IN ('resolved', 'closed')`,
  ];
  const scopeCond = orgScopeCondition(finding.organization_id, options?.scope);
  if (scopeCond) {
    conditions.push(scopeCond);
  }
  return db
    .select({
      severity: finding.severity,
      count: sql<number>`COUNT(*)`.as("count"),
    })
    .from(finding)
    .where(and(...conditions))
    .groupBy(finding.severity);
}

// ─── Trash (L2) ──────────────────────────────────────────────────────────────
// Views of the soft-deleted (deleted_at NOT NULL). Restore = deleted_at NULL.
// In field-check there is NO "delete permanently": the trash only
// restores (immutability/compliance — decision §1b).

export function getTrashedInspectionsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      id: inspection.id,
      status: inspection.status,
      notes: inspection.notes,
      deleted_at: inspection.deleted_at,
      template_name: template.name,
      site_name: site.name,
    })
    .from(inspection)
    .leftJoin(template, eq(inspection.template_id, template.id))
    .leftJoin(site, eq(inspection.site_id, site.id))
    .where(sql`${inspection.deleted_at} IS NOT NULL`)
    .orderBy(desc(inspection.deleted_at));
}

export function getTrashedFindingsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      id: finding.id,
      title: finding.title,
      severity: finding.severity,
      status: finding.status,
      deleted_at: finding.deleted_at,
      site_name: site.name,
    })
    .from(finding)
    .leftJoin(site, eq(finding.site_id, site.id))
    .where(sql`${finding.deleted_at} IS NOT NULL`)
    .orderBy(desc(finding.deleted_at));
}

export function getTrashedTemplatesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      id: template.id,
      name: template.name,
      description: template.description,
      deleted_at: template.deleted_at,
    })
    .from(template)
    .where(sql`${template.deleted_at} IS NOT NULL`)
    .orderBy(desc(template.deleted_at));
}

// ─── Attachments (photos / signature) ────────────────────────────────────────

export function getAttachmentsByAnswerQuery(db: PowerSyncDrizzleDB, answerId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.inspection_answer_id, answerId))
    .orderBy(asc(attachment.created_at));
}

export function getAttachmentsByFindingQuery(db: PowerSyncDrizzleDB, findingId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.finding_id, findingId))
    .orderBy(asc(attachment.created_at));
}

export function getAttachmentsByInspectionQuery(db: PowerSyncDrizzleDB, inspectionId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.inspection_id, inspectionId))
    .orderBy(asc(attachment.created_at));
}

/** Photos of ALL items of an inspection at once (to validate/export). */
export function getAnswerAttachmentsForInspectionQuery(
  db: PowerSyncDrizzleDB,
  inspectionId: string,
) {
  return db
    .select({
      id: attachment.id,
      inspection_answer_id: attachment.inspection_answer_id,
      filename: attachment.filename,
      url: attachment.url,
      content_type: attachment.content_type,
      created_at: attachment.created_at,
    })
    .from(attachment)
    .innerJoin(inspection_answer, eq(attachment.inspection_answer_id, inspection_answer.id))
    .where(eq(inspection_answer.inspection_id, inspectionId))
    .orderBy(asc(attachment.created_at));
}
